import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useScene } from "@/contexts/scene-context"
import { Lightbulb, Palette, Eye, Camera, Maximize, RotateCcw, Zap } from "lucide-react"

export default function ControlPanel() {
  const { sceneState, updateSceneState, resetSceneState } = useScene()

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Scene Controls</h3>
        <Button variant="outline" size="sm" onClick={resetSceneState}>
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset All
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["lighting", "material", "performance"]}>
        {/* Lighting Controls */}
        <AccordionItem value="lighting">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Lighting
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Ambient Light</Label>
                  <span className="text-xs text-muted-foreground">{sceneState.ambientIntensity.toFixed(1)}</span>
                </div>
                <Slider
                  value={[sceneState.ambientIntensity]}
                  onValueChange={([value]) => updateSceneState({ ambientIntensity: value })}
                  max={2}
                  min={0}
                  step={0.1}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Directional Light</Label>
                  <span className="text-xs text-muted-foreground">{sceneState.directionalIntensity.toFixed(1)}</span>
                </div>
                <Slider
                  value={[sceneState.directionalIntensity]}
                  onValueChange={([value]) => updateSceneState({ directionalIntensity: value })}
                  max={3}
                  min={0}
                  step={0.1}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Light Position</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateSceneState({ directionalPosition: [10, 10, 5] })}
                  >
                    Default
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateSceneState({ directionalPosition: [0, 15, 0] })}
                  >
                    Top
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateSceneState({ directionalPosition: [15, 5, 0] })}
                  >
                    Side
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Enable Shadows</Label>
                <Switch
                  checked={sceneState.enableShadows}
                  onCheckedChange={(checked) => updateSceneState({ enableShadows: checked })}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Material Controls */}
        <AccordionItem value="material">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Material
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-sm">Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={sceneState.modelColor}
                    onChange={(e) => updateSceneState({ modelColor: e.target.value })}
                    className="w-full h-8 rounded border border-border bg-transparent"
                  />
                  <Button variant="outline" size="sm" onClick={() => updateSceneState({ modelColor: "#ffffff" })}>
                    Reset
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Opacity</Label>
                  <span className="text-xs text-muted-foreground">{(sceneState.modelOpacity * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[sceneState.modelOpacity]}
                  onValueChange={([value]) => updateSceneState({ modelOpacity: value })}
                  max={1}
                  min={0}
                  step={0.05}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Wireframe</Label>
                <Switch
                  checked={sceneState.wireframe}
                  onCheckedChange={(checked) => updateSceneState({ wireframe: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Enable Reflections</Label>
                <Switch
                  checked={sceneState.enableReflections}
                  onCheckedChange={(checked) => updateSceneState({ enableReflections: checked })}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* View Controls */}
        <AccordionItem value="view">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              View
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSceneState({ cameraPosition: [5, 5, 5] })}
                  className="flex items-center gap-2"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSceneState({ cameraPosition: [0, 10, 0] })}
                  className="flex items-center gap-2"
                >
                  <Camera className="w-3 h-3" />
                  Top View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSceneState({ cameraPosition: [10, 0, 0] })}
                  className="flex items-center gap-2"
                >
                  <Camera className="w-3 h-3" />
                  Side View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateSceneState({ cameraPosition: [0, 0, 10] })}
                  className="flex items-center gap-2"
                >
                  <Camera className="w-3 h-3" />
                  Front View
                </Button>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Zoom Level</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={() => updateSceneState({ cameraPosition: [2, 2, 2] })}
                  >
                    <Maximize className="w-3 h-3 mr-1" />
                    Zoom In
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Enable Heatmap</Label>
                <Switch
                  checked={sceneState.enableHeatmap}
                  onCheckedChange={(checked) => updateSceneState({ enableHeatmap: checked })}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Performance Controls */}
        <AccordionItem value="performance">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Performance
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-sm">Render Quality</Label>
                <Select
                  value={sceneState.renderQuality}
                  onValueChange={(value) =>
                    updateSceneState({
                      renderQuality: value as "low" | "medium" | "high" | "ultra",
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="ultra">Ultra</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Post-Processing</Label>
                <Switch
                  checked={sceneState.enablePostProcessing}
                  onCheckedChange={(checked) => updateSceneState({ enablePostProcessing: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm">Debug Mode</Label>
                <Switch
                  checked={sceneState.debugMode}
                  onCheckedChange={(checked) => updateSceneState({ debugMode: checked })}
                />
              </div>

              <div className="text-xs text-muted-foreground mt-2">
                <p>
                  Press <kbd className="px-1 py-0.5 bg-muted rounded">Ctrl</kbd> +{" "}
                  <kbd className="px-1 py-0.5 bg-muted rounded">P</kbd> to toggle performance stats
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
